import moment from 'moment';

/**
 * Format cap structure data for API
 */
export function formatCapStructureForAPI(capStructure, userData) {
  const formatted = {
    portfolioCompanyId: userData.metaData.portfolioCompanyId,
    capTableDate: null, // Will be set from basicInputs
    status: 'in progress',
  };

  // Format Preferred Shares
  if (capStructure.preferred && capStructure.preferred.length > 0) {
    formatted.preferred = {};
    const usedNames = {};

    capStructure.preferred.forEach((pref) => {
      let key = pref.name;

      // Check for duplicate names and append transaction date
      if (usedNames[key]) {
        key = `${pref.name} - ${pref.transactionDate || 'unknown'}`;
      }
      usedNames[pref.name] = true;

      // Format participation rights to match dropdown values
      // Dropdown expects: 'Fully participating', 'non-participating', 'capped'
      let { participationRights } = pref;
      if (participationRights) {
        const lower = participationRights.toLowerCase();
        if (lower === 'non-participating') {
          participationRights = 'non-participating';
        } else if (lower === 'participating' || lower.includes('fully')) {
          participationRights = 'Fully participating';
        } else if (lower.includes('capped')) {
          participationRights = 'capped';
        }
      } else {
        // Default to non-participating if not provided (lowercase to match dropdown)
        participationRights = 'non-participating';
      }

      // Format dividend rate as decimal string (8 → "0.08")
      let { dividendRate } = pref;
      if (dividendRate !== null && dividendRate > 1) {
        dividendRate = (dividendRate / 100).toString();
      } else if (dividendRate !== null) {
        dividendRate = dividendRate.toString();
      }

      formatted.preferred[key] = {
        transactionDate: pref.transactionDate,
        shares: pref.shares,
        isDividends: pref.mandatoryDividends ? 1 : 0,
        dividendStartDate: pref.dividendStartDate || null,
        dividendRate,
        dividendCompounding: pref.dividendCompounding || 'N/A',
        dividendExpirationDate: pref.dividendExpirationDate,
        issuePrice: pref.issuePrice ? pref.issuePrice.toString() : null,
        liquidationPriority: pref.liquidationPriority,
        liquidationPreference: pref.liquidationPreference,
        participationRights,
        participationCap: pref.participationCap,
        conversionRatio: pref.conversionRatio,
      };
    });
  }

  // Format Common Shares
  if (capStructure.common && capStructure.common.length > 0) {
    formatted.common = {};

    capStructure.common.forEach((common) => {
      formatted.common[common.name] = {
        common: common.amount ? parseInt(common.amount.toString().replace(/[,$]/g, ''), 10) : null,
        issuePrice: null,
        participationRights: null,
        prior: null,
      };
    });
  }

  // Format Warrants
  if (capStructure.warrants && capStructure.warrants.length > 0) {
    formatted.warrant = {};

    capStructure.warrants.forEach((warrant) => {
      const price = warrant.exercisePrice ? parseFloat(warrant.exercisePrice).toFixed(6) : '0.000000';
      const key = `${warrant.name} Warrant @ $${price}`;

      formatted.warrant[key] = {
        quantity: warrant.amount ? warrant.amount.toString() : null,
        strikePrice: price,
        type: warrant.name.includes('Series') ? 'Preferred' : 'Common',
        preferredClass: warrant.name,
      };
    });
  }

  // Format Options
  if (capStructure.options && capStructure.options.length > 0) {
    formatted.option = {};

    capStructure.options.forEach((option) => {
      const price = option.exercisePrice ? parseFloat(option.exercisePrice).toFixed(6) : '0.000000';
      const key = `${option.name} Option @ $${price}`;

      formatted.option[key] = {
        quantity: option.amount ? option.amount.toString() : null,
        strikePrice: price,
        type: option.name.includes('Series') ? 'Preferred' : 'Common',
        preferredClass: option.name,
      };
    });
  }

  // Format Option Pool
  if (capStructure.optionPool !== null) {
    formatted.remainingOptionsPool = capStructure.optionPool;
  }

  return formatted;
}

/**
 * Format basic info for transaction update
 */
export function formatBasicInfoForAPI(basicInputs) {
  return {
    valuationDate: basicInputs.valuationDate,
    transactionDate: basicInputs.transactionDate,
    capTableDate: basicInputs.capTableDate,
    financialStatementDate: basicInputs.lastFinancialStatementDate,
    fiscalYearEnd: basicInputs.fiscalYearEnd,
  };
}

/**
 * Wrap value in object format expected by API
 */
function wrapValue(value) {
  if (value === null || value === undefined) {
    return { value: '' };
  }
  return { value: value.toString() };
}

/**
 * Format financials for transaction update
 */
export function formatFinancialsForAPI(financials) {
  const formatted = {
    clientFinancialInfo: {},
    enterpriseFinancialInfo: {},
  };

  // Balance Sheet -> clientFinancialInfo
  if (financials.balanceSheet) {
    formatted.clientFinancialInfo = {
      totalInvestedCapital: wrapValue(financials.balanceSheet.totalInvestedCapital),
      totalDebt: wrapValue(financials.balanceSheet.totalDebt),
      nonConvertibleDebt: wrapValue(financials.balanceSheet.totalNonConvertibleDebt),
      currentCash: wrapValue(financials.balanceSheet.cashAndEquivalents),
      monthlyCashBurn: wrapValue(financials.balanceSheet.cashRunway),
    };

    // Add debtAsOfTransactionDate to basicInfo (returned separately)
    formatted.debtAsOfTransactionDate = financials.balanceSheet.debtAsOfTransactionDate;

    // Add EBITDAChecked to enterpriseFinancialInfo
    formatted.enterpriseFinancialInfo.EBITDAChecked = financials.balanceSheet.revenueOverOneMillion || false;
  }

  // Income Statement -> enterpriseFinancialInfo
  if (financials.incomeStatement) {
    const periods = [
      { key: 'lfyMinus1', prefix: 'lastFiscalYear', suffix: 'M1' },
      { key: 'lfy', prefix: 'lastFiscalYear', suffix: '' },
      { key: 'ltm', prefix: 'last12Months', suffix: '' },
      { key: 'cfy', prefix: 'currentFiscalYear', suffix: '' },
      { key: 'ntm', prefix: 'next12Months', suffix: '' },
      { key: 'nfy', prefix: 'nextFiscalYear', suffix: '' },
      { key: 'nfyPlus1', prefix: 'nextFiscalYear', suffix: 'P1' },
      { key: 'nfyPlus2', prefix: 'nextFiscalYear', suffix: 'P2' },
    ];

    periods.forEach(({ key, prefix, suffix }) => {
      const period = financials.incomeStatement[key];
      if (period) {
        formatted.enterpriseFinancialInfo[`${prefix}Revenue${suffix}`] = wrapValue(period.revenue);
        formatted.enterpriseFinancialInfo[`${prefix}GrossProfit${suffix}`] = wrapValue(period.grossProfit);
        formatted.enterpriseFinancialInfo[`${prefix}EBITDA${suffix}`] = wrapValue(period.ebitda);
      }
    });
  }

  return formatted;
}

/**
 * Format holdings for transaction update
 */
export function formatHoldingsForAPI(holdings) {
  return holdings.map((holding) => ({
    fundName: holding.fundName,
    classOfStock: holding.classOfStock,
    sharesOwned: holding.sharesOwned,
    pricePerShare: holding.pricePerShare,
    carryingValue: null, // Will be calculated by backend
  }));
}

/**
 * Validate holdings against cap structure
 */
export function validateHoldingsAgainstCapStructure(holdings, capStructure) {
  const errors = [];

  // Build list of available classes from cap structure
  const availableClasses = new Set();

  console.log('=== Validating Holdings Against Cap Structure ===');
  console.log('Building available classes from cap structure...');

  if (capStructure.preferred) {
    console.log(`Found ${capStructure.preferred.length} preferred classes:`);
    capStructure.preferred.forEach((pref) => {
      console.log(`  - "${pref.name}"`);
      availableClasses.add(pref.name);
    });
  }

  if (capStructure.common) {
    console.log(`Found ${capStructure.common.length} common classes:`);
    capStructure.common.forEach((common) => {
      console.log(`  - "${common.name}"`);
      availableClasses.add(common.name);
    });
  }

  console.log(`\nTotal available classes: ${availableClasses.size}`);
  console.log('Available classes:', Array.from(availableClasses));

  // Check each holding
  console.log(`\nValidating ${holdings.length} holdings...`);
  holdings.forEach((holding, index) => {
    console.log(`  Holding ${index}: fundName="${holding.fundName}", classOfStock="${holding.classOfStock}"`);

    if (!availableClasses.has(holding.classOfStock)) {
      console.error(`  ✗ ERROR: Class "${holding.classOfStock}" not found in cap structure!`);

      errors.push({
        index,
        fundName: holding.fundName,
        classOfStock: holding.classOfStock,
        message: `Holdings reference non-existent class '${holding.classOfStock}'. Available classes: ${Array.from(availableClasses).join(', ')}. Please check that the class names in the Holdings section match exactly with the Capital Structure section of your Excel file.`,
      });
    } else {
      console.log(`  ✓ Valid: "${holding.classOfStock}" found in cap structure`);
    }
  });

  console.log('\n=== Validation Complete ===');
  console.log(`Errors: ${errors.length}`);

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Format comps data for API
 */
export function formatCompsForAPI(comps, basicInputs, userData, existingComps) {
  // Convert financialStatementDate to MM/DD/YYYY format
  let financialStatementDate = basicInputs.lastFinancialStatementDate;
  if (financialStatementDate) {
    financialStatementDate = moment(financialStatementDate, 'YYYY-MM-DD').format('MM/DD/YYYY');
  }

  return {
    companyId: userData.metaData.portfolioCompanyId,
    transactionId: userData.transactionData.transactionId,
    enterpriseCompanyId: userData.userAttributes.enterpriseCompanyId,
    transactionDate: basicInputs.transactionDate,
    valuationDate: basicInputs.valuationDate,
    financialStatementDate,
    companies: comps.ciqIds,
    verticals: userData.transactionData.basicInfo?.companyIndustry || [],
    runType: existingComps && existingComps.length > 0 ? 'add' : 'new',
    compData: existingComps || [],
  };
}

/**
 * Build complete transaction update payload
 */
export function buildTransactionUpdatePayload(userData, basicInfo, financials, holdings) {
  const financialData = formatFinancialsForAPI(financials);

  return {
    ...userData.transactionData,
    basicInfo: {
      ...userData.transactionData.basicInfo,
      ...basicInfo,
      ...(financialData.debtAsOfTransactionDate !== undefined && {
        debtAsOfTransactionDate: financialData.debtAsOfTransactionDate,
      }),
    },
    ...(financialData.clientFinancialInfo && Object.keys(financialData.clientFinancialInfo).length > 0 && {
      clientFinancialInfo: {
        ...userData.transactionData.clientFinancialInfo,
        ...financialData.clientFinancialInfo,
      },
    }),
    ...(financialData.enterpriseFinancialInfo && Object.keys(financialData.enterpriseFinancialInfo).length > 0 && {
      enterpriseFinancialInfo: {
        ...userData.transactionData.enterpriseFinancialInfo,
        ...financialData.enterpriseFinancialInfo,
      },
    }),
    ...(holdings && holdings.length > 0 && {
      holdings,
    }),
    version: parseInt(userData.transactionData.version, 10),
  };
}
